// ============================================================================
// AIRearrangeDialog — Iter 62et
// Founder tape en langage naturel comment il veut réorganiser les vidéos.
// L'IA (Claude Sonnet 4.5) renvoie une proposition {task_id → new_due_date}.
// Le founder valide → on applique via l'endpoint existant /replan.
// ============================================================================
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { X, Loader2, Sparkles, ArrowRight, Check, ChevronDown } from './icons';
import { ChevronUp } from 'lucide-react';

const POST_EXAMPLES = [
  'Alterne podcast / facecam, 1 post par jour',
  '1 post tous les 2 jours à partir de demain',
  '2 posts par jour (midi et soir)',
  'Mets les facecams en premier',
];

const EXAMPLES = [
  'Espace toutes les vidéos de 3 jours à partir de la première',
  'Décale tout d\'une semaine',
  'Mets les podcasts en premier, puis les autres',
  'Concentre 2 vidéos par semaine (lundi + jeudi)',
  'Recale sur le mois prochain à partir du 1er',
];

export default function AIRearrangeDialog({ project, onClose, onApplied, scope }) {
  const post = scope === 'post';
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState(null); // null | array
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const generate = async () => {
    const p = prompt.trim();
    if (p.length < 3) { toast.error('Décris comment tu veux ranger'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/projects/${project.id}/ai-rearrange`, { prompt: p, ...(post ? { scope: 'post' } : {}) });
      const list = res.data?.proposals || [];
      if (!list.length) { toast.error('Aucune proposition reçue'); return; }
      // Iter 62eu — Assure que new_position est bien défini pour chaque row
      const withPos = list.map((p, i) => ({
        ...p,
        new_position: typeof p.new_position === 'number' ? p.new_position : i,
      }));
      setProposals(withPos);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur IA');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    if (!proposals?.length) return;
    setApplying(true);
    try {
      // 1) Update dates
      const items = proposals
        .filter((p) => p.new_due_date && p.new_due_date !== p.current_due_date)
        .map((p) => ({ task_id: p.task_id, due_date: p.new_due_date }));
      if (post) {
        for (const it of items) await api.patch(`/tasks/${it.task_id}`, { scheduled_post_date: it.due_date });
        toast.success(`${items.length} post${items.length > 1 ? 's' : ''} réorganisé${items.length > 1 ? 's' : ''} par l'IA`);
        onApplied?.(); onClose();
        return;
      }
      if (items.length) {
        await api.post(`/projects/${project.id}/replan`, { items });
      }
      // 2) Update order (if any new_position provided AND changed)
      const withPos = proposals
        .filter((p) => typeof p.new_position === 'number')
        .sort((a, b) => a.new_position - b.new_position)
        .map((p) => p.task_id);
      const orderChanged = withPos.length === proposals.length &&
        proposals.some((p) => p.new_position !== p.current_position);
      if (orderChanged) {
        try { await api.post(`/projects/${project.id}/reorder-videos`, { order: withPos }); }
        catch (e) { console.warn('reorder skipped', e); }
      }
      toast.success(`${items.length} vidéo${items.length > 1 ? 's' : ''} replanifiée${items.length > 1 ? 's' : ''} par l'IA`);
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible d\'appliquer');
    } finally { setApplying(false); }
  };

  // Iter 62eu — Édition manuelle des propositions IA
  const updateProposal = (idx, patch) => {
    setProposals((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const moveRow = (idx, dir) => {
    setProposals((prev) => {
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      // Re-assign new_position based on new array order (0-indexed)
      return next.map((p, i) => ({ ...p, new_position: i }));
    });
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(6,12,26,0.82)', backdropFilter: 'blur(10px)', display: 'grid', placeItems: 'center', padding: 16, overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" data-testid="ai-rearrange-dialog" style={{ width: '100%', maxWidth: 640, background: 'linear-gradient(180deg, rgba(20,29,66,0.98) 0%, rgba(14,22,44,0.99) 100%)', borderRadius: 22, border: '1px solid rgba(167,139,250,0.25)', boxShadow: '0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(167,139,250,0.15)', padding: 24, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg, rgba(167,139,250,0.22) 0%, rgba(91,190,252,0.18) 100%)', border: '1px solid rgba(167,139,250,0.4)', display: 'grid', placeItems: 'center' }}>
            <Sparkles size={20} style={{ color: '#B294F5' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: '#F0F4FF', margin: 0, letterSpacing: '-0.01em' }}>{post ? 'Rangement des posts par IA' : 'Rangement par IA'}</h2>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 12, color: '#8A96B4', marginTop: 2 }}>{project.name || 'Projet'} · {proposals ? `${proposals.length} vidéos` : 'décris comment tu veux ranger'}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(28,38,80,0.6)', border: '1px solid rgba(50,64,120,0.5)', color: '#F0F4FF', width: 34, height: 34, borderRadius: 10, cursor: 'pointer', display: 'grid', placeItems: 'center' }}><X size={16} /></button>
        </div>

        {!proposals && (
          <>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: '#8A96B4', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Ton instruction</div>
            <textarea
              data-testid="ai-rearrange-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate(); }}
              autoFocus
              placeholder={post ? "Ex : alterne podcast / facecam, 1 post par jour à partir de demain… (tu peux aussi coller un doc)" : "Ex: espace toutes les vidéos de 3 jours à partir du 25 février…"}
              rows={4}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 12, background: 'rgba(28,38,80,0.55)', border: '1px solid rgba(50,64,120,0.6)', color: '#F0F4FF', outline: 'none', fontFamily: 'Sora', fontWeight: 500, fontSize: 14, letterSpacing: '-0.005em', resize: 'vertical', minHeight: 90 }}
            />
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5, color: '#8A96B4', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 6px' }}>Exemples</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
              {(post ? POST_EXAMPLES : EXAMPLES).map((ex) => (
                <button
                  key={ex}
                  type="button"
                  data-testid="ai-rearrange-example"
                  onClick={() => setPrompt(ex)}
                  style={{ padding: '6px 12px', borderRadius: 999, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#B294F5', fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5, cursor: 'pointer', letterSpacing: '-0.005em' }}
                >{ex}</button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
              <button onClick={onClose} style={{ padding: '12px 16px', borderRadius: 12, background: 'transparent', border: '1px solid rgba(50,64,120,0.6)', color: '#F0F4FF', fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Annuler</button>
              <button
                data-testid="ai-rearrange-generate"
                onClick={generate}
                disabled={busy || prompt.trim().length < 3}
                style={{ padding: '12px 16px', borderRadius: 12, background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 100%)', border: 'none', color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: busy ? 'wait' : 'pointer', opacity: busy || prompt.trim().length < 3 ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 8px 22px -6px rgba(167,139,250,0.55)' }}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {busy ? 'L\'IA réfléchit…' : 'Générer la proposition'}
              </button>
            </div>
          </>
        )}

        {proposals && (
          <>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: '#8A96B4', marginBottom: 10 }}>
              Vérifie la proposition. Tu peux <span style={{ color: '#B294F5' }}>modifier une date</span> ou <span style={{ color: '#B294F5' }}>bouger une vidéo</span> si l'IA s'est plantée.
            </div>
            <div data-testid="ai-rearrange-proposals" style={{ maxHeight: '48vh', overflowY: 'auto', borderRadius: 12, border: '1px solid rgba(50,64,120,0.5)', background: 'rgba(11,19,48,0.5)' }}>
              {proposals.map((p, i) => {
                const changed = p.new_due_date && p.new_due_date !== p.current_due_date;
                return (
                  <div key={p.task_id} data-testid={`ai-rearrange-row-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px', borderBottom: i < proposals.length - 1 ? '1px solid rgba(50,64,120,0.3)' : 'none', background: changed ? 'rgba(167,139,250,0.06)' : 'transparent' }}>
                    {/* Position badge + up/down */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <button
                        type="button"
                        data-testid={`ai-rearrange-up-${i}`}
                        onClick={() => moveRow(i, -1)}
                        disabled={i === 0}
                        style={{ width: 22, height: 18, borderRadius: 5, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#B294F5', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.3 : 1, display: 'grid', placeItems: 'center', padding: 0 }}
                        title="Monter"
                      ><ChevronUp size={12} /></button>
                      <div style={{ width: 26, height: 20, borderRadius: 6, background: 'rgba(28,38,80,0.6)', border: '1px solid rgba(50,64,120,0.5)', color: '#F0F4FF', display: 'grid', placeItems: 'center', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 11 }}>{String(i + 1).padStart(2, '0')}</div>
                      <button
                        type="button"
                        data-testid={`ai-rearrange-down-${i}`}
                        onClick={() => moveRow(i, 1)}
                        disabled={i === proposals.length - 1}
                        style={{ width: 22, height: 18, borderRadius: 5, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.28)', color: '#B294F5', cursor: i === proposals.length - 1 ? 'not-allowed' : 'pointer', opacity: i === proposals.length - 1 ? 0.3 : 1, display: 'grid', placeItems: 'center', padding: 0 }}
                        title="Descendre"
                      ><ChevronDown size={12} /></button>
                    </div>
                    {/* Title + format */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: '#F0F4FF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.005em' }}>{p.title}</div>
                      {p.format && <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: '#8A96B4', marginTop: 1 }}>{p.format}</div>}
                    </div>
                    {/* Dates : old (barrée) → new (input éditable) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: '#8A96B4', textDecoration: changed ? 'line-through' : 'none' }}>{p.current_due_date || '—'}</span>
                      <ArrowRight size={11} style={{ color: changed ? '#B294F5' : 'rgba(138,150,180,0.4)' }} />
                      <input
                        data-testid={`ai-rearrange-date-${i}`}
                        type="date"
                        value={p.new_due_date || ''}
                        onChange={(e) => updateProposal(i, { new_due_date: e.target.value || null })}
                        style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 700, color: '#B294F5', background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: 7, padding: '4px 7px', outline: 'none', minWidth: 128, colorScheme: 'dark' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 8, marginTop: 16 }}>
              <button onClick={() => setProposals(null)} disabled={applying} style={{ padding: '11px 12px', borderRadius: 12, background: 'transparent', border: '1px solid rgba(50,64,120,0.6)', color: '#8A96B4', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Recommencer</button>
              <button onClick={onClose} disabled={applying} style={{ padding: '11px 12px', borderRadius: 12, background: 'transparent', border: '1px solid rgba(50,64,120,0.6)', color: '#F0F4FF', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>Annuler</button>
              <button
                data-testid="ai-rearrange-apply"
                onClick={apply}
                disabled={applying}
                style={{ padding: '11px 14px', borderRadius: 12, background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 100%)', border: 'none', color: '#0B1330', fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: applying ? 'wait' : 'pointer', opacity: applying ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 8px 22px -6px rgba(167,139,250,0.55)' }}
              >
                {applying ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                {applying ? 'Application…' : 'Appliquer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
